<template>
  <div class="deal-tasks">
    <button
      v-if="projectOpenTaskCount > 0 && projectId"
      type="button"
      class="deal-tasks__project-link"
      @click="goToProject"
    >
      <BaseIcon name="clipboard" size="14" />
      <span>
        {{ $t('tasks_view.open_project_tasks', { count: projectOpenTaskCount }) }}
      </span>
      <BaseIcon name="chevron" size="12" :rotate="90" />
    </button>

    <form class="deal-tasks__quick-add" @submit.prevent="submitQuickTask">
      <span class="deal-tasks__glyph">
        <img src="/icons/task-plus.svg" alt="" />
      </span>
      <input
        v-model="quickTitle"
        type="text"
        class="deal-tasks__quick-input"
        :placeholder="$t('deals.task_ui.quick_add_placeholder')"
        :disabled="quickSaving"
      />

      <DropdownMenu ref="dueMenuRef" class="deal-tasks__due-menu">
        <template #trigger>
          <button type="button" class="deal-tasks__chip">
            <span class="deal-tasks__glyph">
              <img src="/icons/task-calendar.svg" alt="" />
            </span>
            <span>{{ quickDueLabel }}</span>
          </button>
        </template>
        <template #items>
          <button
            v-for="option in dueOptions"
            :key="option.value"
            type="button"
            class="dropdown-menu__item"
            @click="setQuickDue(option.value)"
          >
            {{ option.label }}
          </button>
          <label class="deal-tasks__date-field">
            <span>{{ $t('deals.task_ui.pick_date') }}</span>
            <input
              type="date"
              :value="quickDueDate"
              @change="setQuickDue($event.target.value)"
            />
          </label>
          <button type="button" class="dropdown-menu__item" @click="openCreateTaskModal">
            {{ $t('deals.task_ui.more_options') }}
          </button>
        </template>
      </DropdownMenu>

      <div class="deal-tasks__chip deal-tasks__chip--assignee">
        <DealOwnerPicker
          show-label
          :owner="quickAssignee"
          :owner-id="quickAssigneeId"
          :members="members"
          :label="quickAssigneeLabel"
          @select="quickAssigneeId = $event"
        />
      </div>

      <button
        type="submit"
        class="deal-tasks__submit"
        :disabled="!quickTitle.trim() || quickSaving"
      >
        {{ $t('deals.task_ui.add') }}
      </button>
    </form>

    <div v-if="loading" class="deal-tasks__loading">
      <div class="deal-tasks__spinner"></div>
    </div>

    <div v-else-if="tasks.length === 0" class="deal-tasks__empty">
      <span class="deal-tasks__empty-icon">
        <img src="/icons/task-empty.svg" alt="" />
      </span>
      <div class="deal-tasks__empty-text">
        <p class="deal-tasks__empty-title">{{ $t('deals.task_ui.empty_title') }}</p>
        <p class="deal-tasks__empty-description">{{ $t('deals.task_ui.empty_description') }}</p>
      </div>
      <div class="deal-tasks__empty-actions">
        <button
          type="button"
          class="deal-tasks__chip deal-tasks__chip--lg"
          :disabled="quickSaving"
          @click="createCallbackTomorrow"
        >
          <span class="deal-tasks__glyph">
            <img src="/icons/task-phone.svg" alt="" />
          </span>
          <span>{{ $t('deals.task_ui.quick_callback') }}</span>
        </button>
        <button
          type="button"
          class="deal-tasks__chip deal-tasks__chip--lg"
          :disabled="quickSaving"
          @click="createProposalFollowUp"
        >
          <span class="deal-tasks__glyph">
            <img src="/icons/task-file.svg" alt="" />
          </span>
          <span>{{ $t('deals.task_ui.quick_follow_up') }}</span>
        </button>
      </div>
    </div>

    <div v-else class="deal-tasks__groups">
      <div
        v-for="group in taskGroups"
        :key="group.key"
        class="deal-tasks__group"
        :class="`deal-tasks__group--${group.key}`"
      >
        <component
          :is="group.collapsible ? 'button' : 'div'"
          :type="group.collapsible ? 'button' : undefined"
          class="deal-tasks__group-header"
          :class="{ 'deal-tasks__group-header--toggle': group.collapsible }"
          @click="group.collapsible && (showCompleted = !showCompleted)"
        >
          <span class="deal-tasks__group-label">{{ group.label }}</span>
          <span class="deal-tasks__group-count">{{ group.tasks.length }}</span>
          <BaseIcon
            v-if="group.collapsible"
            name="chevron"
            size="12"
            :rotate="showCompleted ? 180 : 90"
            class="deal-tasks__group-chevron"
          />
        </component>

        <template v-if="!group.collapsible || showCompleted">
          <div
            v-for="task in group.tasks"
            :key="task.id"
            class="deal-tasks__card"
            :class="{
              'deal-tasks__card--open': task.id === selectedTaskId,
              'deal-tasks__card--completed': isCompleted(task),
            }"
          >
            <div
              role="button"
              tabindex="0"
              class="deal-tasks__row"
              :aria-expanded="task.id === selectedTaskId"
              @click="toggleTask(task)"
              @keydown.enter.self="toggleTask(task)"
            >
              <button
                type="button"
                class="deal-tasks__check"
                :class="{ 'deal-tasks__check--checked': isCompleted(task) }"
                :aria-label="isCompleted(task) ? $t('deals.task_reopened') : $t('deals.task_completed')"
                @click.stop="isCompleted(task) ? reopenTask(task) : completeTask(task)"
              >
                <img v-if="isCompleted(task)" src="/icons/pipeline-action-check.svg" alt="" />
              </button>
              <span class="deal-tasks__name">{{ task.title }}</span>
              <span
                class="deal-tasks__type"
                :class="`deal-tasks__type--${task.task_type || 'general'}`"
              >
                {{ getTaskTypeLabel(task.task_type || 'general') }}
              </span>
              <span v-if="task.priority === 'high'" class="deal-tasks__priority">
                {{ getPriorityLabel(task.priority) }}
              </span>
              <span
                v-if="task.due_date"
                class="deal-tasks__due"
                :class="{ 'deal-tasks__due--overdue': group.key === 'overdue' }"
              >
                {{ formatDueDate(task.due_date, task.due_time) }}
              </span>
              <DealOwnerPicker
                :owner="getAssignee(task)"
                :owner-id="getTaskAssigneeId(task) || null"
                :members="members"
                @select="(userId) => updateTaskAssignee(task, userId)"
              />
              <BaseIcon
                name="chevron"
                size="12"
                :rotate="task.id === selectedTaskId ? 0 : 180"
                class="deal-tasks__row-chevron"
              />
            </div>

            <div v-if="task.id === selectedTaskId && draft" class="deal-tasks__panel">
              <div class="deal-tasks__facts">
                <span class="deal-tasks__fact">
                  <BaseIcon name="user" size="12" />
                  {{ $t('deals.task_ui.created_by', { name: getCreatorName(task) }) }}
                </span>
                <span v-if="task.created_at" class="deal-tasks__fact">
                  <BaseIcon name="clock" size="12" />
                  {{ $t('deals.task_ui.created_at', { date: formatDateTime(task.created_at) }) }}
                </span>
                <span v-if="isCompleted(task) && task.completer" class="deal-tasks__fact">
                  <BaseIcon name="check" size="12" />
                  {{ $t('deals.completed_by') }} {{ getPersonName(task.completer) }}
                </span>
              </div>

              <div class="deal-tasks__form">
                <TextInput
                  :id="`task-${task.id}-title`"
                  v-model="draft.title"
                  :label="$t('tasks_view.fields.name')"
                />
                <TextAreaInput
                  :id="`task-${task.id}-description`"
                  v-model="draft.description"
                  :label="$t('tasks_view.fields.description')"
                  :placeholder="$t('deals.task_ui.description_placeholder')"
                  :rows="3"
                />
                <div class="deal-tasks__form-row">
                  <SelectInput
                    :id="`task-${task.id}-type`"
                    v-model="draft.task_type"
                    :label="$t('tasks_view.fields.type')"
                    :options="typeOptions"
                  />
                  <SelectInput
                    :id="`task-${task.id}-priority`"
                    v-model="draft.priority"
                    :label="$t('tasks_view.fields.priority')"
                    :options="priorityOptions"
                  />
                </div>
                <div class="deal-tasks__form-row">
                  <TextInput
                    :id="`task-${task.id}-due-date`"
                    v-model="draft.due_date"
                    type="date"
                    :label="$t('tasks_view.fields.due_date')"
                  />
                  <TextInput
                    :id="`task-${task.id}-due-time`"
                    v-model="draft.due_time"
                    type="time"
                    :label="$t('tasks_view.fields.due_time')"
                  />
                </div>
              </div>

              <div class="deal-tasks__panel-footer">
                <Btn
                  v-if="leadId"
                  type="button"
                  variant="outlined"
                  size="sm"
                  icon="user"
                  :text="$t('deals.task_ui.open_customer')"
                  @click="$emit('open-customer')"
                />
                <div class="deal-tasks__panel-actions">
                  <Btn
                    v-if="isDraftDirty"
                    type="button"
                    variant="secondary"
                    size="sm"
                    :text="$t('common.cancel')"
                    @click="resetDraft(task)"
                  />
                  <Btn
                    type="button"
                    variant="primary"
                    size="sm"
                    :text="$t('deals.task_ui.save')"
                    :loading="savingTaskId === task.id"
                    :disabled="!isDraftDirty || !draft.title.trim() || !draft.due_date"
                    @click="saveTask(task)"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted, getCurrentInstance, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import { taskService } from '@/services/taskService';
  import { useToastStore } from '@/store/toastStore';
  import { useUserStore } from '@/store/userStore';
  import { useUIStore } from '@/store/uiStore';
  import OrganizationService from '@/services/organizationService';
  import CreateTaskModal from '@/components/tasks/CreateTaskModal.vue';
  import Btn from '@/components/partials/Btn.vue';
  import DropdownMenu from '@/components/partials/DropdownMenu.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import TextAreaInput from '@/components/inputs/TextAreaInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import DealOwnerPicker from '@/components/deals/DealOwnerPicker.vue';

  import BaseIcon from '@shared/components/BaseIcon.vue';

  const EDITABLE_FIELDS = ['title', 'description', 'task_type', 'priority', 'due_date', 'due_time'];

  const toDateKey = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  };

  const dateKeyInDays = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return toDateKey(date);
  };

  export default {
    name: 'DealTasks',

    components: {
      BaseIcon,
      Btn,
      DropdownMenu,
      TextInput,
      TextAreaInput,
      SelectInput,
      DealOwnerPicker,
    },

    props: {
      dealId: {
        type: String,
        required: true,
      },
      initialTaskId: {
        type: String,
        default: null,
      },
      leadId: {
        type: String,
        default: null,
      },
    },

    emits: ['open-customer'],

    setup(props, { expose }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const toastStore = useToastStore();
      const userStore = useUserStore();
      const uiStore = useUIStore();
      const router = useRouter();
      const organizationService = new OrganizationService();

      const loading = ref(false);
      const tasks = ref([]);
      const members = ref([]);
      const showCompleted = ref(false);
      const selectedTaskId = ref(props.initialTaskId);
      const projectOpenTaskCount = ref(0);
      const projectId = ref(null);
      const draft = ref(null);
      const savingTaskId = ref(null);

      const currentUserId = computed(() => userStore.user?.id || userStore.userData?.id || null);

      // ----------------------------------------------------------------------
      // Grouping
      // ----------------------------------------------------------------------

      const isCompleted = (task) => task.status === 'completed' || task.status === 'cancelled';

      const openTasks = computed(() => tasks.value.filter((task) => !isCompleted(task)));
      const completedTasks = computed(() => tasks.value.filter(isCompleted));

      const taskGroups = computed(() => {
        const today = toDateKey(new Date());
        return [
          {
            key: 'overdue',
            label: $t('deals.overdue_tasks'),
            tasks: openTasks.value.filter((task) => task.due_date && task.due_date < today),
          },
          {
            key: 'today',
            label: $t('deals.today_tasks'),
            tasks: openTasks.value.filter((task) => task.due_date === today),
          },
          {
            key: 'upcoming',
            label: $t('deals.upcoming_tasks'),
            tasks: openTasks.value.filter((task) => !task.due_date || task.due_date > today),
          },
          {
            key: 'completed',
            label: $t('deals.completed_tasks'),
            tasks: completedTasks.value,
            collapsible: true,
          },
        ].filter((group) => group.tasks.length);
      });

      // ----------------------------------------------------------------------
      // People
      // ----------------------------------------------------------------------

      const getPersonName = (person) => {
        if (!person) return '';
        return (
          [person.first_name, person.last_name].filter(Boolean).join(' ').trim() ||
          person.name ||
          person.email ||
          ''
        );
      };

      const getCreatorName = (task) =>
        getPersonName(task.creator) || $t('deals.task_ui.unknown_user');

      const getAssignee = (task) => task.assignee || task.assigned_to_user || null;

      const getTaskAssigneeId = (task) =>
        task?.assigned_to || task?.assignee?.id || task?.assigned_to_user?.id || '';

      const getMemberUser = (userId) => {
        const member = members.value.find((item) => {
          const user = item.user || item;
          return (user?.id ?? item.user_id) === userId;
        });
        return member ? member.user || member : null;
      };

      // ----------------------------------------------------------------------
      // Quick add
      // ----------------------------------------------------------------------

      const dueMenuRef = ref(null);
      const quickTitle = ref('');
      const quickDueDate = ref(dateKeyInDays(1));
      const quickAssigneeId = ref(currentUserId.value);
      const quickSaving = ref(false);

      const quickAssignee = computed(() => {
        if (!quickAssigneeId.value) return null;
        return (
          getMemberUser(quickAssigneeId.value) ||
          (quickAssigneeId.value === currentUserId.value ? userStore.userData : null)
        );
      });

      const quickAssigneeLabel = computed(() => {
        if (!quickAssigneeId.value) return '';
        if (quickAssigneeId.value === currentUserId.value) return $t('deals.task_ui.me');
        return quickAssignee.value?.first_name || getPersonName(quickAssignee.value);
      });

      const formatShortDate = (dateKey) =>
        new Date(`${dateKey}T00:00:00`).toLocaleDateString('nl-NL', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
        });

      const quickDueLabel = computed(() => {
        if (quickDueDate.value === dateKeyInDays(0)) return $t('deals.task_ui.today');
        if (quickDueDate.value === dateKeyInDays(1)) return $t('deals.task_ui.tomorrow');
        return formatShortDate(quickDueDate.value);
      });

      const dueOptions = computed(() => [
        { value: dateKeyInDays(0), label: $t('deals.task_ui.today') },
        { value: dateKeyInDays(1), label: $t('deals.task_ui.tomorrow') },
        { value: dateKeyInDays(3), label: $t('deals.task_ui.in_three_days') },
        { value: dateKeyInDays(7), label: $t('deals.task_ui.next_week') },
      ]);

      const setQuickDue = (value) => {
        if (value) quickDueDate.value = value;
        dueMenuRef.value?.closeDropdown();
      };

      const addCreatedTask = (task) => {
        if (!task?.id) return;
        tasks.value = [task, ...tasks.value.filter((item) => item.id !== task.id)];
      };

      const createTask = async (payload) => {
        quickSaving.value = true;
        try {
          const task = await taskService.createDealTask(props.dealId, {
            task_type: 'general',
            priority: 'medium',
            assigned_to: currentUserId.value,
            source: 'manual',
            ...payload,
          });
          addCreatedTask(task);
          toastStore.addToast({ type: 'success', message: $t('deals.task_created') });
          return task;
        } catch (error) {
          toastStore.addToast({ type: 'error', message: $t('deals.task_create_failed') });
          return null;
        } finally {
          quickSaving.value = false;
        }
      };

      const submitQuickTask = async () => {
        const title = quickTitle.value.trim();
        if (!title || quickSaving.value) return;
        const task = await createTask({
          title,
          due_date: quickDueDate.value,
          assigned_to: quickAssigneeId.value || null,
        });
        if (task) quickTitle.value = '';
      };

      const createCallbackTomorrow = () =>
        createTask({
          title: $t('deals.task_ui.quick_callback_title'),
          task_type: 'callback',
          due_date: dateKeyInDays(1),
        });

      const createProposalFollowUp = () =>
        createTask({
          title: $t('deals.task_ui.quick_follow_up_title'),
          task_type: 'follow_up',
          due_date: dateKeyInDays(3),
        });

      const openCreateTaskModal = () => {
        dueMenuRef.value?.closeDropdown();
        const modalId = uiStore.addModal(CreateTaskModal, {
          title: $t('deals.new_task'),
          size: 'md',
          context: 'deal',
          dealId: props.dealId,
          initialTitle: quickTitle.value.trim(),
          defaultAssignedTo: quickAssigneeId.value,
          onSubmit: async (task) => {
            uiStore.closeModal(modalId);
            quickTitle.value = '';
            if (task) {
              addCreatedTask(task);
            } else {
              await fetchTasks();
            }
          },
        });
      };

      // ----------------------------------------------------------------------
      // Expanded task editing
      // ----------------------------------------------------------------------

      const selectedTask = computed(
        () => tasks.value.find((task) => task.id === selectedTaskId.value) || null
      );

      const toDraft = (task) => ({
        title: task.title || '',
        description: task.description || '',
        task_type: task.task_type || 'general',
        priority: task.priority || 'medium',
        due_date: task.due_date || '',
        due_time: task.due_time ? task.due_time.slice(0, 5) : '',
      });

      const resetDraft = (task) => {
        draft.value = task ? toDraft(task) : null;
      };

      const isDraftDirty = computed(() => {
        if (!draft.value || !selectedTask.value) return false;
        const original = toDraft(selectedTask.value);
        return EDITABLE_FIELDS.some((field) => (draft.value[field] || '') !== original[field]);
      });

      const toggleTask = (task) => {
        selectedTaskId.value = selectedTaskId.value === task.id ? null : task.id;
      };

      watch(selectedTaskId, () => resetDraft(selectedTask.value));

      const replaceTask = (taskId, changes) => {
        const index = tasks.value.findIndex((item) => item.id === taskId);
        if (index !== -1) {
          tasks.value[index] = { ...tasks.value[index], ...changes };
        }
      };

      const saveTask = async (task) => {
        if (!draft.value || !isDraftDirty.value) return;
        savingTaskId.value = task.id;
        try {
          const payload = {
            title: draft.value.title.trim(),
            description: draft.value.description.trim() || null,
            task_type: draft.value.task_type || 'general',
            priority: draft.value.priority || 'medium',
            due_date: draft.value.due_date,
            due_time: draft.value.due_time || null,
          };
          const updated = await taskService.updateTask(task.id, payload);
          replaceTask(task.id, updated?.id ? updated : payload);
          resetDraft(selectedTask.value);
          toastStore.addToast({ type: 'success', message: $t('deals.task_ui.saved') });
        } catch (error) {
          toastStore.addToast({ type: 'error', message: $t('deals.task_update_failed') });
        } finally {
          savingTaskId.value = null;
        }
      };

      // ----------------------------------------------------------------------
      // Status & assignment
      // ----------------------------------------------------------------------

      const completeTask = async (task) => {
        try {
          await taskService.completeTask(task.id);
          replaceTask(task.id, { status: 'completed', completed_at: new Date().toISOString() });
          toastStore.addToast({ type: 'success', message: $t('deals.task_completed') });
        } catch (error) {
          toastStore.addToast({ type: 'error', message: $t('deals.task_update_failed') });
        }
      };

      const reopenTask = async (task) => {
        try {
          await taskService.reopenTask(task.id);
          replaceTask(task.id, { status: 'pending', completed_at: null });
          toastStore.addToast({ type: 'success', message: $t('deals.task_reopened') });
        } catch (error) {
          toastStore.addToast({ type: 'error', message: $t('deals.task_update_failed') });
        }
      };

      const updateTaskAssignee = async (task, userId) => {
        const assignedTo = userId || null;
        try {
          await taskService.updateTask(task.id, { assigned_to: assignedTo });
          const assignee = assignedTo ? getMemberUser(assignedTo) : null;
          replaceTask(task.id, { assigned_to: assignedTo, assignee, assigned_to_user: assignee });
          toastStore.addToast({ type: 'success', message: $t('tasks_view.assign_success') });
        } catch (error) {
          toastStore.addToast({ type: 'error', message: $t('tasks_view.assign_failed') });
        }
      };

      // ----------------------------------------------------------------------
      // Formatting
      // ----------------------------------------------------------------------

      const formatDueDate = (date, time) => {
        if (!date) return '';
        const shortTime = time ? time.slice(0, 5) : '';
        if (date === dateKeyInDays(0)) return shortTime || $t('common.today');
        if (date === dateKeyInDays(1)) {
          return [$t('common.tomorrow'), shortTime].filter(Boolean).join(' ');
        }
        return [formatShortDate(date), shortTime].filter(Boolean).join(' ');
      };

      const formatDateTime = (value) =>
        new Date(value).toLocaleString('nl-NL', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });

      const getPriorityLabel = (priority) => {
        const labels = {
          high: $t('deals.priority_high'),
          medium: $t('deals.priority_medium'),
          low: $t('deals.priority_low'),
        };
        return labels[priority] || priority;
      };

      const getTaskTypeLabel = (taskType) => {
        const labels = {
          callback: $t('deals.task_type_callback'),
          follow_up: $t('deals.task_type_follow_up'),
          general: $t('deals.task_type_general'),
        };
        return labels[taskType] || taskType;
      };

      const typeOptions = computed(() =>
        ['general', 'callback', 'follow_up'].map((value) => ({
          value,
          label: getTaskTypeLabel(value),
        }))
      );

      const priorityOptions = computed(() =>
        ['low', 'medium', 'high'].map((value) => ({ value, label: getPriorityLabel(value) }))
      );

      // ----------------------------------------------------------------------
      // Data loading
      // ----------------------------------------------------------------------

      const fetchTasks = async () => {
        loading.value = true;
        try {
          const response = await taskService.getDealTasks(props.dealId);
          tasks.value = response.tasks || [];
          resetDraft(selectedTask.value);
        } catch (error) {
          console.error('Failed to fetch tasks:', error);
        } finally {
          loading.value = false;
        }
      };

      const fetchMembers = async () => {
        const organizationId = userStore.currentOrganization?.id;
        if (!organizationId) return;
        try {
          const response = await organizationService.getMembers(organizationId);
          members.value = Array.isArray(response) ? response : response?.data || [];
        } catch (error) {
          console.error('Failed to fetch task assignee options:', error);
          members.value = [];
        }
      };

      const fetchProjectOpenCount = async () => {
        try {
          const result = await taskService.getDealProjectOpenTaskCount(props.dealId);
          projectOpenTaskCount.value = result.count || 0;
          projectId.value = result.project_id || null;
        } catch (error) {
          projectOpenTaskCount.value = 0;
          projectId.value = null;
        }
      };

      const goToProject = () => {
        if (!projectId.value) return;
        router.push({ name: 'ProjectDetail', params: { id: projectId.value } });
      };

      onMounted(() => {
        fetchTasks();
        fetchMembers();
        fetchProjectOpenCount();
      });

      watch(
        () => props.initialTaskId,
        (taskId) => {
          selectedTaskId.value = taskId;
        }
      );

      watch(
        () => props.dealId,
        () => {
          selectedTaskId.value = null;
          fetchTasks();
          fetchProjectOpenCount();
        }
      );

      watch(currentUserId, (userId) => {
        if (!quickAssigneeId.value) quickAssigneeId.value = userId;
      });

      expose({ openCreateTaskModal, addCreatedTask });

      return {
        loading,
        tasks,
        members,
        taskGroups,
        showCompleted,
        selectedTaskId,
        projectOpenTaskCount,
        projectId,
        draft,
        savingTaskId,
        isDraftDirty,
        dueMenuRef,
        quickTitle,
        quickDueDate,
        quickDueLabel,
        quickAssignee,
        quickAssigneeId,
        quickAssigneeLabel,
        quickSaving,
        dueOptions,
        typeOptions,
        priorityOptions,
        isCompleted,
        getPersonName,
        getCreatorName,
        getAssignee,
        getTaskAssigneeId,
        setQuickDue,
        submitQuickTask,
        createCallbackTomorrow,
        createProposalFollowUp,
        openCreateTaskModal,
        toggleTask,
        resetDraft,
        saveTask,
        completeTask,
        reopenTask,
        updateTaskAssignee,
        goToProject,
        formatDueDate,
        formatDateTime,
        getPriorityLabel,
        getTaskTypeLabel,
      };
    },
  };
</script>

<style scoped lang="scss">
  .deal-tasks {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;

    &__project-link {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      border: 1px solid #e3e6ee;
      border-radius: 4px;
      background: #fff;
      color: #3364e6;
      font: inherit;
      font-size: 13px;
      font-weight: 500;
      text-align: left;
      cursor: pointer;

      span {
        flex: 1;
      }

      @include hover {
        border-color: #d7d7d7;
      }
    }

    // Figma 106:1338 — quick add bar
    &__quick-add {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
      padding: 8px 8px 8px 16px;
      border: 1px solid #d7d7d7;
      border-radius: 4px;
      background: #fff;

      &:focus-within {
        border-color: #3364e6;
      }
    }

    &__glyph {
      display: inline-flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;

      img {
        display: block;
      }
    }

    &__quick-input {
      flex: 1 1 160px;
      min-width: 0;
      padding: 0;
      border: 0;
      background: transparent;
      color: #000921;
      font: inherit;
      font-size: 13px;
      line-height: 20px;

      &::placeholder {
        color: #434f6f;
      }

      &:focus {
        outline: none;
      }
    }

    &__chip {
      display: inline-flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      gap: 6px;
      height: 28px;
      padding: 0 12px;
      border: 1px solid #d7d7d7;
      border-radius: 4px;
      background: #fff;
      color: #434f6f;
      font: inherit;
      font-size: 13px;
      font-weight: 500;
      line-height: 20px;
      white-space: nowrap;
      cursor: pointer;

      @include hover {
        background: rgb(0 9 33 / 4%);
      }

      &:disabled {
        cursor: wait;
        opacity: 0.6;
      }

      &--lg {
        height: 36px;
      }

      &--assignee {
        :deep(.deal-owner-picker__trigger) {
          margin: 0;
          padding: 0;
          background: transparent;
        }

        :deep(.deal-owner-picker__label) {
          color: #434f6f;
        }

        :deep(.deal-owner-picker__chevron) {
          display: none;
        }
      }
    }

    &__due-menu {
      :deep(.dropdown-menu__content) {
        min-width: 180px;
        padding: 6px;
        border-color: #d7d7d7;
        border-radius: 4px;
      }

      :deep(.dropdown-menu__item) {
        border: 0;
        background: transparent;
        color: #000921;
        font: inherit;
        font-size: 13px;
        font-weight: 400;
      }
    }

    &__date-field {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin: 4px 0;
      padding: 8px;
      border-top: 1px solid #e3e6ee;
      border-bottom: 1px solid #e3e6ee;
      color: #434f6f;
      font-size: 11px;

      input {
        height: 28px;
        padding: 0 8px;
        border: 1px solid #d7d7d7;
        border-radius: 4px;
        color: #000921;
        font: inherit;
        font-size: 13px;
      }
    }

    &__submit {
      display: inline-flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      height: 28px;
      padding: 0 12px;
      border: 1px solid #000921;
      border-radius: 4px;
      background: #000921;
      color: #fff;
      font: inherit;
      font-size: 13px;
      font-weight: 500;
      line-height: 20px;
      cursor: pointer;

      &:disabled {
        cursor: not-allowed;
        opacity: 0.4;
      }
    }

    // Figma 106:1368 — empty state
    &__empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 40px 32px;
      border: 1px solid #d7d7d7;
      border-radius: 4px;
      background: #fff;
      text-align: center;
    }

    &__empty-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 20px;
      background: #e3e6ee;

      img {
        display: block;
      }
    }

    &__empty-text {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      max-width: 440px;
    }

    &__empty-title {
      margin: 0;
      color: #000921;
      font-size: 13px;
      font-weight: 500;
    }

    &__empty-description {
      margin: 0;
      color: #434f6f;
      font-size: 13px;
      font-weight: 400;
      line-height: 20px;
    }

    &__empty-actions {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 8px;
    }

    &__loading {
      display: flex;
      justify-content: center;
      padding: 32px;
    }

    &__spinner {
      width: 24px;
      height: 24px;
      border: 2px solid #e3e6ee;
      border-top-color: #3364e6;
      border-radius: 50%;
      animation: deal-tasks-spin 1s linear infinite;
    }

    @keyframes deal-tasks-spin {
      to {
        transform: rotate(360deg);
      }
    }

    // Task groups (journey-actions visual language)
    &__groups {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    &__group {
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 10px;
      border-radius: 6px;
      background: #f6f7fa;
    }

    &__group-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 2px 4px 6px;
      border: 0;
      background: transparent;
      font: inherit;
      text-align: left;

      &--toggle {
        cursor: pointer;
      }
    }

    &__group-label {
      flex: 1;
      color: #000921;
      font-size: 13px;
      font-weight: 500;
    }

    &__group--overdue &__group-label {
      color: #d92d20;
    }

    &__group-count {
      color: #434f6f;
      font-size: 13px;
      font-weight: 300;
    }

    &__group-chevron {
      color: #434f6f;
    }

    &__card {
      border: 1px solid #e3e6ee;
      border-radius: 4px;
      background: #fff;

      @include hover {
        border-color: #d7d7d7;
      }

      &--open {
        border-color: #d7d7d7;
        box-shadow: 0 2px 6px rgb(0 9 33 / 6%);
      }

      &--completed .deal-tasks__name {
        color: #8a93a8;
        text-decoration: line-through;
      }
    }

    &__row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 10px;
      cursor: pointer;

      &:focus-visible {
        outline: 2px solid #3364e6;
        outline-offset: 2px;
      }
    }

    &__check {
      display: inline-flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      padding: 0;
      border: 1px solid #d7d7d7;
      border-radius: 50%;
      background: #fff;
      cursor: pointer;

      @include hover {
        border-color: #3364e6;
      }

      &--checked {
        border-color: #3364e6;
        background: #3364e6;
      }

      img {
        display: block;
      }
    }

    &__name {
      flex: 1;
      min-width: 0;
      color: #000921;
      font-size: 13px;
      font-weight: 500;
      @include ellipsis(1);
    }

    &__type,
    &__priority {
      flex-shrink: 0;
      padding: 1px 6px;
      border-radius: 4px;
      background: #f0f2f7;
      color: #434f6f;
      font-size: 11px;
      font-weight: 500;
      line-height: 16px;
    }

    &__type--callback {
      background: #e6e9ff;
      color: #3364e6;
    }

    &__type--follow_up {
      background: #fff4e5;
      color: #b45f00;
    }

    &__priority {
      background: #fdecea;
      color: #d92d20;
    }

    &__due {
      flex-shrink: 0;
      color: #434f6f;
      font-size: 11px;
      font-weight: 300;

      &--overdue {
        color: #d92d20;
        font-weight: 500;
      }
    }

    &__row-chevron {
      flex-shrink: 0;
      color: #8a93a8;
    }

    // Expanded panel
    &__panel {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 12px 10px;
      border-top: 1px solid #e3e6ee;
    }

    &__facts {
      display: flex;
      flex-wrap: wrap;
      gap: 6px 16px;
    }

    &__fact {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      color: #434f6f;
      font-size: 12px;

      .icon {
        color: #8a93a8;
      }
    }

    &__form {
      display: flex;
      flex-direction: column;
      gap: 8px;

      :deep(.form-group) {
        margin-bottom: 0;
      }
    }

    &__form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__panel-footer {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }

    &__panel-actions {
      display: flex;
      gap: 8px;
      margin-left: auto;
    }
  }
</style>
